// Modal popup centered on screen — shows a remark's extract image
// (.context-main) + a discussion thread (.context-discussion, 420px wide).
// Replaces the alpha.10 RemarkDetail side-rail per user feedback:
// "devrait avoir un popup quand on clique sur les boxs".
//
// Open via state.activeRemarkId (set by RectOverlay click OR Kanban card click).
// Close via × button OR ESC OR click on backdrop. DOM/CSS mirror legacy
// stbarts (index.html .context-overlay block, L594-1038).

import type { Remark } from '@ladd-screenshot/shared';
import { For, Show, createMemo, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { api } from '../api/client';
import { actions, stores } from '../state/store';

interface ExtractRef {
  filename?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fullWidth?: boolean;
}

interface RemarkWithCounts extends Remark {
  commentCount?: number;
  historyCount?: number;
}

const STATUS_OPTIONS = ['open', 'pending', 'blocked', 'resolved'] as const;
const STATUS_LABELS: Record<string, string> = {
  open: 'À traiter',
  todo: 'À traiter',
  blocked: 'Bloqué',
  wontfix: 'Bloqué',
  pending: 'À valider',
  doing: 'À valider',
  resolved: 'Résolu',
  done: 'Résolu',
};

// Legacy stbarts header date format: "M/D/YYYY" (US locale, no zero-pad).
function formatDate(at: number): string {
  const d = new Date(at);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}

// Legacy comment date format: "M/D/YYYY HH:MM AM/PM" used in the discussion list.
function formatCommentDate(at: number): string {
  const d = new Date(at);
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()} ${String(h).padStart(2, '0')}:${mm} ${ampm}`;
}

export function ContextOverlayPopup() {
  const active = createMemo(() =>
    stores.state.remarks.find((r) => r.id === stores.state.activeRemarkId) ?? null,
  );

  return (
    <Show when={active()}>
      {(remark) => <ContextOverlayInner remark={remark() as RemarkWithCounts} />}
    </Show>
  );
}

function ContextOverlayInner(props: { remark: RemarkWithCounts }) {
  const [editing, setEditing] = createSignal(false);
  const [draftText, setDraftText] = createSignal(props.remark.text);
  const [commentDraft, setCommentDraft] = createSignal('');
  const [submittingComment, setSubmittingComment] = createSignal(false);
  const [commentError, setCommentError] = createSignal<string | null>(null);

  const isGlobal = createMemo(
    () => (props.remark.resolutionName ?? '') === 'global',
  );
  const remarkNumber = createMemo(() => {
    const idx = stores.state.remarks.findIndex((r) => r.id === props.remark.id);
    return idx >= 0 ? idx + 1 : 1;
  });
  const remarkIndex = remarkNumber;

  const extract = createMemo<ExtractRef | null>(() =>
    props.remark.extract ? (props.remark.extract as ExtractRef) : null,
  );

  const [extractUrl] = createResource(
    () => ({
      captureId: props.remark.captureId ?? null,
      filename: extract()?.filename ?? null,
    }),
    async (req) => {
      if (!req.captureId || !req.filename) return null;
      try {
        const res = await fetch(
          `/api/v1/captures/${encodeURIComponent(req.captureId)}/extract/${encodeURIComponent(req.filename)}`,
          {
            headers: localStorage.getItem('ladd-screenshot.token')
              ? { Authorization: `Bearer ${localStorage.getItem('ladd-screenshot.token')}` }
              : {},
          },
        );
        if (!res.ok) return null;
        return URL.createObjectURL(await res.blob());
      } catch {
        return null;
      }
    },
  );

  onCleanup(() => {
    const u = extractUrl();
    if (u) URL.revokeObjectURL(u);
  });

  onMount(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    onCleanup(() => window.removeEventListener('keydown', handler));
  });

  function close() {
    actions.closeRemarkDetail();
  }

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) close();
  }

  async function saveText() {
    if (draftText().trim() === props.remark.text) {
      setEditing(false);
      return;
    }
    try {
      await actions.updateRemark(props.remark.id, { text: draftText().trim() });
      setEditing(false);
    } catch {
      // keep editing open
    }
  }

  async function changeStatus(newStatus: string) {
    try {
      await actions.updateRemark(props.remark.id, {
        status: newStatus as RemarkWithCounts['status'],
      });
    } catch {
      // ignore — let the UI re-derive from store
    }
  }

  async function submitComment(e: Event) {
    e.preventDefault();
    setCommentError(null);
    const body = commentDraft().trim();
    if (!body) return;
    const author = stores.auth.email;
    if (!author) {
      setCommentError('Session expirée');
      return;
    }
    setSubmittingComment(true);
    try {
      await actions.addComment(props.remark.id, { author, text: body });
      setCommentDraft('');
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Envoi impossible');
    } finally {
      setSubmittingComment(false);
    }
  }

  async function deleteRemark() {
    if (!confirm('Supprimer cette remark ?')) return;
    try {
      await actions.deleteRemark(props.remark.id);
      close();
    } catch (err) {
      console.warn('[ContextOverlay] delete failed', err);
    }
  }

  const canEdit = () => stores.auth.email === props.remark.author;

  // Read comments list embedded in the enriched remark payload (Phase 11 will
  // expose a dedicated GET /:id/comments endpoint; until then the in-memory
  // shape may be empty even when commentCount > 0).
  const comments = createMemo(() => {
    const r = props.remark as RemarkWithCounts & { comments?: Array<{
      id: string;
      author: string;
      text: string;
      createdAt: number;
    }> };
    return r.comments ?? [];
  });

  // Highlight rect coords on the extract image: derived from remark.rect minus
  // extract.y offset (legacy stbarts pattern, the extract is a vertical crop).
  const highlightStyle = createMemo(() => {
    const r = props.remark.rect as
      | { x?: number; y?: number; width?: number; height?: number }
      | undefined;
    const e = extract();
    if (!r || !e) return null;
    const offsetY = e.y ?? 0;
    return {
      left: `${r.x ?? 0}px`,
      top: `${(r.y ?? 0) - offsetY}px`,
      width: `${r.width ?? 0}px`,
      height: `${r.height ?? 0}px`,
    };
  });

  void api;

  return (
    <div class="context-overlay" onClick={handleBackdrop}>
      <div class={`context-content ${isGlobal() ? 'global-remark' : ''}`}>
        <div class="context-header">
          <div class="remark-info">
            <div class="remark-author-line">
              <span class={`remark-number ${props.remark.status}`}>
                {remarkIndex()}
              </span>
              <Show when={isGlobal()}>
                <span class="global-badge">GLOBAL</span>
              </Show>
              <span class="remark-date">{formatDate(props.remark.createdAt)}</span>
            </div>
          </div>
          <button class="close-btn" title="Fermer (ESC)" onClick={close}>
            ×
          </button>
        </div>

        <div class="context-body">
          <Show when={!isGlobal()}>
            <div class="context-main">
              <div class="context-image">
                <Show
                  when={extractUrl()}
                  fallback={
                    <div class="no-image">
                      Extrait indisponible (capture ou crop manquant)
                    </div>
                  }
                >
                  <img src={extractUrl() ?? ''} alt="Contexte capture" />
                  <Show when={highlightStyle()}>
                    <div class="highlight-rect" style={highlightStyle() ?? {}} />
                  </Show>
                </Show>
              </div>
            </div>
          </Show>

          <div class="context-discussion">
            <div class="discussion-header">
              Discussion
              <span class="comment-count">
                {props.remark.commentCount ?? comments().length}
              </span>
            </div>

            <div class="discussion-list">
              <div class="remark-original">
                <div class="comment-header">
                  <span class="comment-author">
                    {props.remark.author.split('@')[0] ?? props.remark.author}
                  </span>
                  <span class="comment-date">{formatDate(props.remark.createdAt)}</span>
                  <Show when={canEdit() && !editing()}>
                    <button
                      class="btn-edit-remark"
                      onClick={() => {
                        setDraftText(props.remark.text);
                        setEditing(true);
                      }}
                      title="Éditer la remark"
                    >
                      ✎
                    </button>
                  </Show>
                </div>
                <Show
                  when={!editing()}
                  fallback={
                    <div class="remark-text-edit">
                      <textarea
                        value={draftText()}
                        onInput={(e) => setDraftText(e.currentTarget.value)}
                      />
                      <div class="edit-actions">
                        <button
                          class="btn-cancel-edit"
                          type="button"
                          onClick={() => setEditing(false)}
                        >
                          Annuler
                        </button>
                        <button
                          class="btn-save-edit"
                          type="button"
                          onClick={() => void saveText()}
                        >
                          Enregistrer
                        </button>
                      </div>
                    </div>
                  }
                >
                  <div class="remark-text-display">{props.remark.text}</div>
                </Show>
              </div>

              <Show when={comments().length > 0}>
                <div class="discussion-separator">COMMENTS ({comments().length})</div>
                <For each={comments()}>
                  {(c) => (
                    <div class="comment-item">
                      <div class="comment-header">
                        <span class="comment-author">
                          {c.author.split('@')[0] ?? c.author}
                        </span>
                        <span class="comment-date">{formatCommentDate(c.createdAt)}</span>
                      </div>
                      <div class="comment-text">{c.text}</div>
                    </div>
                  )}
                </For>
              </Show>

              <Show when={comments().length === 0 && (props.remark.commentCount ?? 0) === 0}>
                <div class="discussion-empty">No comments yet. Add one below.</div>
              </Show>
            </div>

            <form class="discussion-form" onSubmit={submitComment}>
              <textarea
                placeholder="Add a comment..."
                value={commentDraft()}
                onInput={(e) => setCommentDraft(e.currentTarget.value)}
                disabled={submittingComment()}
              />
              <Show when={commentError()}>
                <p class="comments-error">{commentError()}</p>
              </Show>
              <div class="form-actions">
                <button type="submit" disabled={submittingComment() || !commentDraft().trim()}>
                  {submittingComment() ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>

            <div class="discussion-actions">
              <Show when={canEdit()}>
                <button type="button" class="btn-delete" onClick={() => void deleteRemark()}>
                  Delete
                </button>
              </Show>
              <select
                class={`status-select status-${props.remark.status}`}
                value={props.remark.status}
                onChange={(e) => void changeStatus(e.currentTarget.value)}
              >
                <For each={STATUS_OPTIONS}>
                  {(s) => (
                    <option value={s}>{STATUS_LABELS[s] ?? s}</option>
                  )}
                </For>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
